import React, { useContext, useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import {
  BranchColumnsEnum,
  SortDirections,
  ThemeColorsEnum,
} from "../../constants/enums";
import {
  AuthContext,
  LoadingContext,
  ModalContext,
} from "../../context/contextProvider";
import { Search, SearchFilterRequest } from "../../models/searchFilterRequest";

import BusinessIcon from "@mui/icons-material/Business";
import DashboardIcon from "@mui/icons-material/Dashboard";
import { Navigate } from "react-router-dom";
import AddButton from "../../components/buttons/AddButton";
import Card from "../../components/cards/Card";
import EmptyDataBox from "../../components/dataTables/EmptyDataBox";
import NSDataTable from "../../components/dataTables/NSDataTable";
import NSDataTablePagination from "../../components/dataTables/NSDataTablePagination";
import { AppRoutes } from "../../constants/appRoutes";
import Constants from "../../constants/constants";
import useBranchesDataTable from "../../hooks/useBranchesDataTable";
import useCurrentUserInfo from "../../hooks/useCurrentUserInfo";
import Services from "../../services/services";
import Page from "../Page";
import BranchDetails from "./BranchDetails";

const title = "Branches";
const breadcrumbs = [
  {
    IconComponent: DashboardIcon,
    title: "Dashhboard",
    to: AppRoutes.dashboard,
  },
  {
    IconComponent: BusinessIcon,
    title: title,
  },
];

const BranchesPage = (props) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(Constants.defaults.rowsPerPage);
  const [paginationData, setPaginationData] = useState({
    from: null,
    pages: null,
    records: null,
    to: null,
  });
  const [branches, setBranches] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [sortOptions, setSortOptions] = useState({
    column: BranchColumnsEnum.name,
    direction: SortDirections.ascending,
  });

  const { currentClient } = useContext(AuthContext);
  const { setLoading } = useContext(LoadingContext);
  const { showModal } = useContext(ModalContext);

  const userInfo = useCurrentUserInfo();
  const dataTableInfo = useBranchesDataTable(handleDeleted);

  useEffect(
    () => {
      getBranchesAsync((currentPage - 1) * pageSize, pageSize, searchValue);
      return () => {};
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentClient, currentPage, pageSize, searchValue, sortOptions]
  );

  const getBranchesAsync = async (
    start = 0,
    take = Constants.defaults.rowsPerPage,
    searchText = null
  ) => {
    // filter
    const filter = new SearchFilterRequest();
    filter.search = new Search(searchText);
    filter.start = start;
    filter.length = take;
    filter.order[0].column = sortOptions.column ?? BranchColumnsEnum.name;
    filter.order[0].dir = sortOptions.direction ?? SortDirections.ascending;

    setLoading(true);
    const response = await Services.api.clients.getClients(filter);
    setLoading(false);

    if (!response.ok)
      return Services.utility.toast(
        "Could not fetch the branches",
        ThemeColorsEnum.error
      );

    setBranches(response.data?.data);
    setPaginationData({
      from: (currentPage - 1) * pageSize + 1,
      pages: response.data?.pagesTotal,
      records: response.data?.recordsTotal,
      to:
        currentPage * pageSize > response.data?.recordsTotal
          ? response.data?.recordsTotal
          : currentPage * pageSize,
    });
  };

  function handleDeleted(branch) {
    setBranches(branches.filter((x) => x.id !== branch.id));
  }

  const handlePageChange = async (page) => {
    setCurrentPage(page);
  };

  const handleRowClick = (event, row) => {
    event.stopPropagation();
    // opend the details modal
    showModal(row.fullName, <BranchDetails branch={row} />, [
      {
        color: ThemeColorsEnum.quaternary,
        title: "Close",
      },
    ]);
  };

  const handleRowsPerPageChange = (newPerPage, currentPage) => {
    setCurrentPage(1);
    setPageSize(newPerPage);
  };

  const handleSearch = (searchValue) => {
    setSearchValue(searchValue);
  };

  const handleSort = (column, direction) => {
    setCurrentPage(1);
    setSortOptions({ column: column, direction: direction });
  };

  if (!userInfo?.isAdmin)
    return (
      <Navigate
        to={{
          pathname: AppRoutes.forbidden,
        }}
      />
    );

  return (
    <Page breadcrumbs={breadcrumbs} title={title}>
      <Row>
        <Col>
          <Card>
            <Card.Body className="overflow-auto">
              <div className="dataTables_wrapper dt-bootstrap5 no-footer">
                {/* **** BOOTSTRAP PAGINATION **** */}
                <NSDataTablePagination
                  from={paginationData?.from}
                  lastPage={paginationData?.pages}
                  page={currentPage}
                  pageSize={pageSize}
                  total={paginationData?.records}
                  to={paginationData?.to}
                  onPageChange={handlePageChange}
                  onPageSizeChange={(newPerPage) =>
                    handleRowsPerPageChange(newPerPage, currentPage)
                  }
                />
                {/* **** DATA TABLE **** */}
                <NSDataTable
                  buttons={
                    <AddButton
                      state={{
                        branch: userInfo.isSuperAdmin
                          ? null
                          : {
                              fee: currentClient?.fee,
                              name: currentClient?.name,
                              parentClientId:
                                currentClient?.parentClientId ??
                                currentClient?.id,
                            },
                      }}
                      to={AppRoutes.branches_create}
                    />
                  }
                  className="dataTable table-no-more"
                  // dense
                  columns={dataTableInfo.columns}
                  conditionalRowStyles={dataTableInfo.conditionalRowStyles}
                  data={branches}
                  emptyDataComponent={<EmptyDataBox />}
                  onRowClick={handleRowClick}
                  onSearch={handleSearch}
                  onSort={handleSort}
                  paginate
                  search
                  sortBy={sortOptions.column}
                  sortDirection={sortOptions.direction}
                />
                {/* **** BOOTSTRAP PAGINATION **** */}
                <NSDataTablePagination
                  from={paginationData?.from}
                  lastPage={paginationData?.pages}
                  page={currentPage}
                  pageSize={pageSize}
                  showRecordsInfo
                  total={paginationData?.records}
                  to={paginationData?.to}
                  onPageChange={handlePageChange}
                  onPageSizeChange={(newPerPage) =>
                    handleRowsPerPageChange(newPerPage, currentPage)
                  }
                />
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Page>
  );
};

export default BranchesPage;
